// Stockfish: the worker, the queue in front of it, and the three questions anybody asks it.
//
// It was a page module that also happened to own the engine, which is why the only way to rate a
// move was to be the board page. The tools coming next need it too — the one you play against a
// sparring partner needs it to *move*, not only to judge — so the engine is a service now, with no
// DOM in it. What is left in legacy/engine.js is the panel on the board page.
//
// Stockfish stays out of the bundler: it is started with `new Worker("/vendor/stockfish-19/...")`
// and its loader demands a sibling file literally named stockfish.wasm.

import { store } from "../state/index.ts";
import { live } from "../state/live.ts";
import { createEngineQueue } from "../domain/engineQueue.ts";
import type { AnalyseHandle, AnalyseRequest, UciInfo } from "../domain/engineQueue.ts";

/** Stockfish builds — update this when upgrading Stockfish. Each lives in its own folder. */
export const ENGINE_BUILDS: Record<string, EngineBuild> = {
    // "browser" stays the default key so already stored configurations keep resolving.
    "browser": {
        label: "Stockfish 19 (lite)",
        version: "19",
        path: "/vendor/stockfish-19/lite/stockfish.js",
        sizeHint: "~2MB",
        firstLoadTimeoutMs: 60000,
        loadTimeoutMs: 30000,
    },
    "browser-full": {
        label: "Stockfish 19 (full)",
        version: "19",
        path: "/vendor/stockfish-19/full/stockfish.js",
        sizeHint: "~95MB",
        firstLoadTimeoutMs: 300000,
        loadTimeoutMs: 120000,
    },
};

export interface EngineBuild {
    label: string;
    version: string;
    path: string;
    sizeHint: string;
    firstLoadTimeoutMs: number;
    loadTimeoutMs: number;
}

const DEFAULT_BUILD = "browser";

/** What the engine knows about a position, once it has looked. */
export interface Analysis {
    uci: string;
    cp?: number;
    mate?: number;
    depth: number;
}

/** How a move compares to the best one in the same position. */
export interface MoveRating {
    bestUci: string;
    best: { cp?: number; mate?: number };
    played: { cp?: number; mate?: number };
    depth: number;
    sameAsBest: boolean;
}

const KNOWN_ENOUGH_DEPTH = 12;
const RATING_DEPTH = 16;
const BEST_MOVETIME_MS = 900;
const PAIRED_MOVETIME_MS = 1300;
const ANALYSIS_CACHE_SIZE = 200;

/** Best move known per position, fed by every finished search, so rating a move is often instant. */
const analysisByFen = new Map<string, Analysis>();

/** Raw engine lines, for the debug panel. */
const listeners = new Set<(line: string) => void>();

const queue = createEngineQueue({
    send: (command) => sendCommand(command),
    onStuck: () => {
        console.error("[engine] no answer to stop, recreating the worker");
        dispose();
    },
});

// ---------------------------------------------------------------------------------------------
// The worker
// ---------------------------------------------------------------------------------------------

function isBrowserBuild(source: string): boolean {
    return Object.hasOwn(ENGINE_BUILDS, source);
}

/** The build a source names, falling back to the lite one every configuration resolves to. */
export function buildOf(source: string | null | undefined): EngineBuild & { key: string } {
    const key = source && isBrowserBuild(source) ? source : DEFAULT_BUILD;
    return { key, ...ENGINE_BUILDS[key]! };
}

const loadedKey = (build: { key: string; version: string }) => `sparring_engine_${build.key}_v${build.version}`;

function isFirstLoad(build: { key: string; version: string }): boolean {
    try {
        return !localStorage.getItem(loadedKey(build));
    } catch (err) {
        return true;
    }
}

function markLoaded(build: { key: string; version: string }): void {
    try {
        localStorage.setItem(loadedKey(build), "1");
    } catch (err) {
        // A browser that will not remember it only means the next load looks like the first
    }
}

export function sendCommand(command: string): void {
    live.engineWorker?.postMessage(command);
}

/** Every line the engine writes, for whoever is showing them. */
export function onEngineLine(listener: (line: string) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/**
 * The engine, started if it is not running yet. Everything else waits on this, so the first thing
 * that needs it pays for the download and the rest join the same promise.
 */
export function ensure(source?: string | null): Promise<void> {
    if (live.engineReady) return live.engineReady as Promise<void>;

    const build = buildOf(source);
    const first = isFirstLoad(build);
    store.batch((set) => {
        set("engine.activeBuild", build.key);
        set("engine.identity", null);
        set("engine.loadState", "loading");
    });

    console.log("[engine] creating Worker for", build.path, "crossOriginIsolated=", self.crossOriginIsolated);
    const worker = new Worker(build.path);
    live.engineWorker = worker;

    let rejectReady: ((reason: Error) => void) | null = null;
    const ready = new Promise<void>((resolve, reject) => {
        rejectReady = reject;
        const timeoutMs = first ? build.firstLoadTimeoutMs : build.loadTimeoutMs;
        const timeout = setTimeout(() => {
            cleanup();
            console.error("[engine] init timeout after", timeoutMs, "ms — Worker never replied uciok/readyok");
            fail("Engine load timed out. Try refreshing the page.");
            reject(new Error("Browser engine init timed out"));
        }, timeoutMs);

        const cleanup = () => {
            clearTimeout(timeout);
            worker.removeEventListener("message", handshake);
        };

        const handshake = (event: MessageEvent) => {
            const text = String(event.data || "").trim();
            console.log("[engine] <-", text);
            if (text.startsWith("id name ")) {
                // Authoritative version, straight from the engine itself
                store.set("engine.identity", text.slice("id name ".length).trim());
                return;
            }
            if (text === "uciok") {
                sendCommand("isready");
                return;
            }
            if (text === "readyok") {
                cleanup();
                markLoaded(build);
                // From here on every line belongs to the queue's current search
                worker.addEventListener("message", (e) => queue.handleLine(String(e.data || "").trim()));
                store.batch((set) => {
                    set("engine.error", null);
                    set("engine.loadState", "ready");
                });
                resolve();
            }
        };

        worker.addEventListener("message", handshake);
        sendCommand("uci");
        sendCommand("isready");
    });

    live.engineReady = ready;

    worker.addEventListener("messageerror", (e) => console.error("[engine] messageerror", e));
    worker.addEventListener("error", (err) => {
        console.error("[engine] Worker error event", err, err?.message, err?.filename, err?.lineno);
        // A worker that dies mid-search never sends its bestmove: without this the queue would wait
        // for it forever and every later rating would pile up behind it.
        dispose();
        fail("Engine failed to load. Check browser compatibility.");
        rejectReady?.(new Error("Engine failed to load"));
    });

    // Whoever is showing what the engine says gets every line of it
    worker.addEventListener("message", (event) => {
        const text = String(event.data || "").trim();
        listeners.forEach((listener) => listener(text));
    });

    return ready;
}

function fail(message: string): void {
    store.batch((set) => {
        set("engine.error", message);
        set("engine.loadState", "error");
    });
}

/** Tears the worker down so the next search starts the build the settings now name. */
export function dispose(): void {
    const worker = live.engineWorker;
    if (!worker) return;
    queue.cancelAll("engine disposed");
    worker.terminate();
    live.engineWorker = null;
    live.engineReady = null;
    analysisByFen.clear();
    store.batch((set) => {
        set("engine.activeBuild", null);
        set("engine.loadState", "idle");
        set("engine.identity", null);
        set("engine.latestScorePawns", null);
        set("engine.latestDepth", null);
        set("engine.isEvaluating", false);
    });
}

/** True once the engine is up. Whoever cannot wait for it carries on without it. */
export async function warmUp(source?: string | null): Promise<boolean> {
    try {
        await ensure(source);
        return true;
    } catch (err) {
        return false;
    }
}

// ---------------------------------------------------------------------------------------------
// What it is asked
// ---------------------------------------------------------------------------------------------

/** A search, straight through the queue: one at a time, rating jobs before the screen's. */
export function analyse(request: AnalyseRequest): AnalyseHandle {
    return queue.analyse(request);
}

export function cancelAll(reason: string): void {
    queue.cancelAll(reason);
}

/** Remembers the best move of a position, and keeps the cache from growing without end. */
function remember(fen: string, info: UciInfo): Analysis | null {
    if (!info?.pv || !info.depth) return null;
    const known = analysisByFen.get(fen);
    if (known && known.depth > info.depth) return known;
    const entry: Analysis = { uci: info.pv.split(" ")[0]!, cp: info.scoreCp, mate: info.mate, depth: info.depth };
    analysisByFen.delete(fen);
    analysisByFen.set(fen, entry);
    if (analysisByFen.size > ANALYSIS_CACHE_SIZE) {
        analysisByFen.delete(analysisByFen.keys().next().value as string);
    }
    return entry;
}

/** What the engine already knows about a position, if it has looked at it. */
export function knownAnalysis(fen: string): Analysis | null {
    return analysisByFen.get(fen) ?? null;
}

/** Lets the engine think about a position while the user decides, so rating their move is instant. */
export async function prefetchPosition(fen: string): Promise<void> {
    if (!(await warmUp())) return;
    const known = analysisByFen.get(fen);
    if (known && known.depth >= RATING_DEPTH) return;
    const result = await analyse({
        fen, depth: RATING_DEPTH, priority: "prefetch", group: "prefetch", key: `pre|${fen}`,
        onInfo: (info) => { if (info.multipv === 1) remember(fen, info); },
    }).promise;
    if (!result.cancelled && result.lines?.length) remember(fen, result.lines[0]!);
}

/**
 * Rates a move against the best one in the SAME position: both scores come from one search of
 * `fenBefore` restricted to the two moves, so they share depth and hash and need no colour flipping.
 * Reading "the eval after the move" from the screen instead compared two unrelated searches, and by
 * the time it was read the sparring partner had usually already replied.
 */
export async function rateMove(fenBefore: string, uci: string): Promise<MoveRating | null> {
    if (!(await warmUp())) return null;

    let best = analysisByFen.get(fenBefore);
    if (!best || best.depth < KNOWN_ENOUGH_DEPTH) {
        const result = await analyse({
            fen: fenBefore, depth: 14, movetime: BEST_MOVETIME_MS, priority: "rate", key: `best|${fenBefore}`,
        }).promise;
        if (result.cancelled || !result.lines?.length) return best ? pairedSearch(fenBefore, uci, best) : null;
        best = remember(fenBefore, result.lines[0]!) ?? undefined;
        if (!best) return null;
    }
    if (best.uci === uci) {
        const score = { cp: best.cp, mate: best.mate };
        return { bestUci: uci, best: score, played: score, depth: best.depth, sameAsBest: true };
    }
    return pairedSearch(fenBefore, uci, best);
}

async function pairedSearch(fenBefore: string, uci: string, best: Analysis): Promise<MoveRating | null> {
    const depth = Math.min(RATING_DEPTH, Math.max(KNOWN_ENOUGH_DEPTH, best.depth));
    const result = await analyse({
        fen: fenBefore, depth, movetime: PAIRED_MOVETIME_MS, multiPV: 2,
        searchmoves: [best.uci, uci], priority: "rate",
    }).promise;
    if (result.cancelled) return null;
    const lineOf = (move: string) => (result.lines || []).find((line) => line.pv?.split(" ")[0] === move);
    const bestLine = lineOf(best.uci);
    const playedLine = lineOf(uci);
    if (!bestLine || !playedLine) return null;
    return {
        bestUci: best.uci, depth: result.depth ?? best.depth, sameAsBest: false,
        best: { cp: bestLine.scoreCp, mate: bestLine.mate },
        played: { cp: playedLine.scoreCp, mate: playedLine.mate },
    };
}

/** Stockfish plays at a limited strength when asked; left alone it plays as hard as it can. */
export function strengthOptions(elo: number | null | undefined): string[] {
    if (!elo) return ["setoption name UCI_LimitStrength value false"];
    const clamped = Math.max(MIN_ELO, Math.min(MAX_ELO, Math.round(elo)));
    return [
        "setoption name UCI_LimitStrength value true",
        `setoption name UCI_Elo value ${clamped}`,
    ];
}

/** What Stockfish accepts for UCI_Elo. Asking outside it is asking for the nearest end of it. */
export const MIN_ELO = 1320;
export const MAX_ELO = 3190;

/**
 * The move the engine would play here. This is what a game against it is made of, so it takes a
 * strength: without one it plays at full strength, which is nobody's sparring partner.
 */
export async function bestMove(fen: string, { elo = null, movetime = 500, depth = null }: {
    elo?: number | null; movetime?: number; depth?: number | null;
} = {}): Promise<string | null> {
    if (!(await warmUp())) return null;
    const result = await analyse({
        fen,
        movetime: depth ? undefined : movetime,
        depth: depth ?? undefined,
        priority: "rate",
        group: "play",
        options: strengthOptions(elo),
    }).promise;
    return result.cancelled ? null : (result.bestmove ?? null);
}

// Exposed like the store and the live objects are: for the browser console, and for the walk that
// has to ask the engine something without going through a page.
window.__engine = { ensure, warmUp, analyse, rateMove, bestMove, prefetchPosition, dispose, ENGINE_BUILDS };
